// Auth API client —— 包装 /auth/* HTTP 调用。
// 仅 fetch + JSON，不知道 React。Tauri 端将来需走 IPC 时再加适配器。

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  /** K5: 用户自定义微信号 (全站唯一 case-insensitive) */
  username: string | null;
  /** K4: 头像 URL, 形如 "/avatars/usr_XXX.png?v=<ts>", null=未设 */
  avatar_url: string | null;
}

export interface MyPubkey {
  id: number;
  pubkey_b64: string;
  device_label: string | null;
  created_at: number;
  revoked_at: number | null;
}

export class AuthApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly serverMessage: string,
  ) {
    super(`auth api ${status}: ${serverMessage}`);
  }
}

export class AuthNetworkError extends Error {
  constructor(
    public readonly url: string,
    cause: unknown,
  ) {
    super(`无法连接认证服务: ${url} (${cause instanceof Error ? cause.message : String(cause)})`);
  }
}

const PROD_AUTH_BASE = "https://chatbox.jennieapp.com";

/** 推断 auth endpoints base URL（同 inferRelayUrl 思路） */
export function inferAuthBase(): string {
  const explicit = new URLSearchParams(globalThis.location?.search ?? "").get(
    "auth",
  );
  if (explicit) return explicit.replace(/\/$/, "");
  if (isPackagedTauri()) return PROD_AUTH_BASE;
  const loc = globalThis.location;
  if (!loc || loc.protocol === "file:") return "http://localhost:8787";
  if (loc.hostname === "localhost" || loc.hostname === "127.0.0.1")
    return "http://localhost:8787";
  return `${loc.protocol}//${loc.host}`;
}

function isPackagedTauri(): boolean {
  const w = globalThis.window as
    | (Window & { __TAURI_INTERNALS__?: unknown; __TAURI__?: unknown })
    | undefined;
  return Boolean((w?.__TAURI_INTERNALS__ || w?.__TAURI__) && import.meta.env.PROD);
}

async function call<T>(
  base: string,
  path: string,
  init: RequestInit,
): Promise<T> {
  const url = `${base}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(init.headers ?? {}),
      },
    });
  } catch (err) {
    throw new AuthNetworkError(url, err);
  }
  const body = await res.text();
  if (!res.ok) {
    let msg = body;
    try {
      const j = JSON.parse(body) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      /* not json */
    }
    throw new AuthApiError(res.status, msg);
  }
  if (!body) return undefined as unknown as T;
  return JSON.parse(body) as T;
}

/** 发送 magic link 请求（POST /auth/magic-link/request） */
export async function requestMagicLink(email: string): Promise<{
  ok: true;
  expires_in: number;
}> {
  const base = inferAuthBase();
  return await call(base, "/auth/magic-link/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/** 用 session token 拿当前用户（GET /auth/me） */
export async function fetchMe(session: string): Promise<{ user: AuthUser }> {
  const base = inferAuthBase();
  return await call(base, "/auth/me", {
    method: "GET",
    headers: { Authorization: `Bearer ${session}` },
  });
}

/** 注销（POST /auth/logout） */
export async function logout(session: string): Promise<void> {
  const base = inferAuthBase();
  await call<{ ok: true }>(base, "/auth/logout", {
    method: "POST",
    headers: { Authorization: `Bearer ${session}` },
  });
}

// ---------- H9 密码登录 ----------

interface SignInResponse {
  user: AuthUser;
  session: string;
}

/** POST /auth/password/signup —— 新邮箱注册 */
export async function passwordSignup(
  email: string,
  password: string,
): Promise<SignInResponse> {
  const base = inferAuthBase();
  return await call(base, "/auth/password/signup", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

/** POST /auth/password/login —— 邮箱+密码登录 */
export async function passwordLogin(
  email: string,
  password: string,
): Promise<SignInResponse> {
  const base = inferAuthBase();
  return await call(base, "/auth/password/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

/** POST /auth/password/set —— 已登录用户设置/修改密码 */
export async function setPassword(
  session: string,
  password: string,
): Promise<void> {
  const base = inferAuthBase();
  await call<{ ok: true }>(base, "/auth/password/set", {
    method: "POST",
    headers: { Authorization: `Bearer ${session}` },
    body: JSON.stringify({ password }),
  });
}

/** POST /auth/password/forgot —— 发"重置密码"邮件 */
export async function requestPasswordReset(email: string): Promise<{
  ok: true;
  expires_in: number;
}> {
  const base = inferAuthBase();
  return await call(base, "/auth/password/forgot", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

// ---------- K7 Room adapter 元信息 ----------

export interface UsageOption {
  label: string;
  template: string;
  description?: string;
}

export interface RoomAdapter {
  id: string;
  description?: string | null;
  usages: UsageOption[];
  trust_scope: string;
}

/** GET /api/rooms/:room/adapters —— 列 Room 内 agent 暴露的 adapter + 二级菜单 */
export async function getRoomAdapters(roomId: string): Promise<RoomAdapter[]> {
  const base = inferAuthBase();
  try {
    const r = await call<{ adapters: RoomAdapter[] }>(
      base,
      `/api/rooms/${encodeURIComponent(roomId)}/adapters`,
      { method: "GET" },
    );
    return r.adapters;
  } catch {
    return [];
  }
}

// ---------- K6 公开 profile ----------

export interface PublicProfile {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
}

/** GET /auth/users/:id/profile —— 公开，Room peer 显头像/昵称用 */
export async function getUserProfile(userId: string): Promise<PublicProfile | null> {
  const base = inferAuthBase();
  try {
    return await call<PublicProfile>(base, `/auth/users/${encodeURIComponent(userId)}/profile`, {
      method: "GET",
    });
  } catch {
    return null;
  }
}

// ---------- K4 头像 ----------

/** POST /auth/me/avatar JSON {data_url:"data:image/png;base64,..."} */
export async function uploadMyAvatar(
  session: string,
  dataUrl: string,
): Promise<AuthUser> {
  const base = inferAuthBase();
  const r = await call<{ user: AuthUser }>(base, "/auth/me/avatar", {
    method: "POST",
    headers: { Authorization: `Bearer ${session}` },
    body: JSON.stringify({ data_url: dataUrl }),
  });
  return r.user;
}

/** DELETE /auth/me/avatar —— 清除当前头像 */
export async function deleteMyAvatar(session: string): Promise<AuthUser> {
  const base = inferAuthBase();
  const r = await call<{ user: AuthUser }>(base, "/auth/me/avatar", {
    method: "DELETE",
    headers: { Authorization: `Bearer ${session}` },
  });
  return r.user;
}

// ---------- K5 username (微信号) ----------

/** 客户端 = 服务端规则 (微信号风) */
export function validateUsernameClient(s: string): string | null {
  const n = [...s].length;
  if (n < 6 || n > 20) return "用户名长度必须 6-20 字符";
  if (!/^[a-zA-Z]/.test(s)) return "必须以字母开头";
  if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(s))
    return "仅允许字母 / 数字 / 下划线 / 短横线";
  return null;
}

export async function setMyUsername(
  session: string,
  username: string,
): Promise<AuthUser> {
  const base = inferAuthBase();
  const r = await call<{ user: AuthUser }>(base, "/auth/me/username", {
    method: "POST",
    headers: { Authorization: `Bearer ${session}` },
    body: JSON.stringify({ username }),
  });
  return r.user;
}

export interface UsernameAvail {
  available: boolean;
  reason?: string;
}

export async function checkUsernameAvailable(
  username: string,
): Promise<UsernameAvail> {
  const base = inferAuthBase();
  return await call<UsernameAvail>(
    base,
    `/auth/users/username-available/${encodeURIComponent(username)}`,
    { method: "GET" },
  );
}

// ---------- K3 update profile ----------

/** POST /auth/me/profile —— 更新 display_name。null/空 = 清空昵称 */
export async function updateMyProfile(
  session: string,
  display_name: string | null,
): Promise<AuthUser> {
  const base = inferAuthBase();
  const r = await call<{ user: AuthUser }>(base, "/auth/me/profile", {
    method: "POST",
    headers: { Authorization: `Bearer ${session}` },
    body: JSON.stringify({ display_name }),
  });
  return r.user;
}

// ---------- F2 user pubkey ----------

/** POST /auth/me/pubkey —— 上传/更新当前设备 pubkey（idempotent） */
export async function uploadMyPubkey(
  session: string,
  pubkey_b64: string,
  device_label?: string,
): Promise<MyPubkey> {
  const base = inferAuthBase();
  return await call<MyPubkey>(base, "/auth/me/pubkey", {
    method: "POST",
    headers: { Authorization: `Bearer ${session}` },
    body: JSON.stringify({ pubkey_b64, device_label }),
  });
}

/** GET /auth/me/pubkeys —— 列我的所有 pubkey */
export async function listMyPubkeys(session: string): Promise<MyPubkey[]> {
  const base = inferAuthBase();
  const r = await call<{ pubkeys: MyPubkey[] }>(base, "/auth/me/pubkeys", {
    method: "GET",
    headers: { Authorization: `Bearer ${session}` },
  });
  return r.pubkeys;
}

/** DELETE /auth/me/pubkey/:pubkey_b64 —— 撤销某把 key */
export async function revokeMyPubkey(
  session: string,
  pubkey_b64: string,
): Promise<void> {
  const base = inferAuthBase();
  await call<{ ok: true }>(
    base,
    `/auth/me/pubkey/${encodeURIComponent(pubkey_b64)}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${session}` },
    },
  );
}
